import { DataListItem } from '../components/DataList/DataList';
import { DialGroup } from './DialGroupService';
import MeasurementNameMap from '../components/MeasurementsComponent/measurementNameMap';

interface WellnessReferenceItem {
    name: string,
    unit?: string,
    valuePostfix?: string,
    description?: string,
    hidden?: boolean,
    decimalPlaces?: number,
    min?: number,
    max?: number,
}

export interface Measurement {
    key: string;
    value: number;
}

export interface WellnessItem {
    name: string,
    value: number,
    formattedValue: string,
    formattedWithPostfix: string,
    unitLabel?: string,
    min?: number | undefined,
    max?: number | undefined,
    dialGroup?: DialGroup | undefined,
}

export interface SizeMatchRecommendation {
    garmentType: string,
    productName: string,
    productCode: string,
    recommendation: string,
    gender: string,
}

export interface BodyComposition {
    bodyMassGrams?: number,
    volumeCubicMillimeters?: number,
    bodyFatMass?: number | null,
    fatFreeMass?: number | null,
    totalBodyWaterMass?: number | null,
    totalBodyProteinMass?: number | null,
    totalBodyBoneMineralMass?: number | null,
    bodyCellMass?: number | null,
    intraCellularWaterMass?: number | null,
    extraCellularWaterMass?: number | null,
    fatFreeExtraCellularSolidsMass?: number | null,
    intraCellularProteinMass?: number | null,
    extraCellularProteinMass?: number | null,
    basalMetabolicRate?: number | null,
    bodySurfaceArea?: number | null,
    waistToHip?: number | null,
    waistToHeight?: number | null,
    waistToThigh?: number | null,
    waistToCalf?: number | null,
    bodyMassIndex?: number | null,
    bodyRoundnessIndex?: number | null,
    bodyAdiposityIndex?: number | null,
    abdominalVolumeIndex?: number | null,
    conicityIndex?: number | null,
}

export const mapBodyCompToModel = (measurements: Measurement[]) => {
    if (!measurements || measurements.length === 0) {
        return undefined;
    }

    // Create a lookup map for faster access
    const measurementMap = new Map<string, number>();
    measurements.forEach((m) => {
        if (m.key) {
            measurementMap.set(m.key, m.value ?? 0);
        }
    });

    const getValue = (key: string): number | undefined => {
        const value = measurementMap.get(key);
        return value !== undefined ? value : 0;
    };

    return {
        bodyMassGrams: getValue('bodyMassGrams'),
        volumeCubicMillimeters: getValue('volumeCubicMillimeters'),

        bodyFatMass: getValue('bodyFatMass'),
        fatFreeMass: getValue('fatFreeMass'),
        totalBodyWaterMass: getValue('totalBodyWaterMass'),
        totalBodyProteinMass: getValue('totalBodyProteinMass'),
        totalBodyBoneMineralMass: getValue('totalBodyBoneMineralMass'),
        bodyCellMass: getValue('bodyCellMass'),
        intraCellularWaterMass: getValue('intraCellularWaterMass'),
        extraCellularWaterMass: getValue('extraCellularWaterMass'),
        fatFreeExtraCellularSolidsMass: getValue('fatFreeExtraCellularSolidsMass'),
        intraCellularProteinMass: getValue('intraCellularProteinMass'),
        extraCellularProteinMass: getValue('extraCellularProteinMass'),
        basalMetabolicRate: getValue('basalMetabolicRate'),

        bodySurfaceArea: getValue('bodySurfaceArea'),
        waistToHip: getValue('waistToHip'),
        waistToHeight: getValue('waistToHeight'),
        waistToThigh: getValue('waistToThigh'),
        waistToCalf: getValue('waistToCalf'),
        bodyMassIndex: getValue('bodyMassIndex'),
        bodyRoundnessIndex: getValue('bodyRoundnessIndex'),
        bodyAdiposityIndex: getValue('bodyAdiposityIndex'),
        abdominalVolumeIndex: getValue('abdominalVolumeIndex'),
        conicityIndex: getValue('conicityIndex'),
    };
};

const wellnessReference: { [key: string]: WellnessReferenceItem } = {
    gender: { name: 'Gender', hidden: true },
    weight: { name: 'Weight', unit: 'kg', valuePostfix: ' kg' },
    height: { name: 'Height', unit: 'cm', valuePostfix: ' cm' },
    heartRate: { name: 'Heart Rate', unit: 'beats/min', valuePostfix: ' beats/min', min: 0, max: 140 },
    breathing: { name: 'Breathing', unit: 'breaths/min', valuePostfix: ' breaths/min', min: 0, max: 35 },
    systolicBP: { name: 'Systolic BP (standing)', valuePostfix: ' mmHg', decimalPlaces: 0 },
    diastolicBP: { name: 'Diastolic BP (standing)', valuePostfix: ' mmHg', decimalPlaces: 0 },
    heartRateVariability: { name: 'Heart Rate Variability', unit: 'ms', valuePostfix: ' ms', min: 1, max: 80 },
    irregularHeartBeats: { name: 'Irregular heartbeat Count', valuePostfix: '', decimalPlaces: 0 },
    cardiacWorkload: { name: 'Cardiac Workload', unit: 'dB', valuePostfix: ' dB', min: 3.71, max: 4.28 },
    vascularCapacity: { name: 'Vascular Capacity', unit: 'sec', valuePostfix: ' sec', min: 0.3, max: 3 },
    bmi: { name: 'Body Mass Index', unit: 'kg/m²', valuePostfix: ' kg/m²', min: 10, max: 60 },
    waistToHeight: { name: 'Waist-to-Height Ratio', unit: '%', valuePostfix: '%', min: 25, max: 70 },
    bodyShape: { name: 'Body Shape Index', valuePostfix: '/8.83', decimalPlaces: 2, min: 6.19, max: 8.83 },
    facialAge: { name: 'Facial Skin Age', valuePostfix: ' years', decimalPlaces: 0 },
    cardioDiseaseRisk: { name: 'Cardiac Disease Risk', valuePostfix: '%' },
    heartAttackRisk: { name: 'Heart Attack Risk', valuePostfix: '%' },
    strokeRisk: { name: 'Stroke Risk', valuePostfix: '%' },
    hyperTensionRisk: { name: 'Hypertension Risk', valuePostfix: '%' },
    diabetesRisk: { name: 'Type 2 Diabetes Risk', valuePostfix: '%' },
    hypercholesterolemiaRisk: { name: 'Hypercholesterolemia Risk', valuePostfix: '%' },
    hypertriglyceridemiaRisk: { name: 'Hypertriglyceridemia Risk', valuePostfix: '%' },
    hemoglobinA1cRisk: { name: 'Hemoglobin A1c Risk', valuePostfix: '%' },
    fastingBCRisk: { name: 'Fasting Blood Glucose Risk', valuePostfix: '%' },
    vitalScore: { name: 'Vitals', valuePostfix: '/5.0', decimalPlaces: 1 },
    physicalScore: { name: 'Physical', valuePostfix: '/5.0', decimalPlaces: 1 },
    physiologicalScore: { name: 'Physiological', valuePostfix: '/5.0', decimalPlaces: 1 },
    mentalStressIndex: { name: 'Mental Stress Index', valuePostfix: '/5.9', decimalPlaces: 1 },
    mentalScore: { name: 'Mental', valuePostfix: '/5.0', decimalPlaces: 1 },
    risksScore: { name: 'Risks', valuePostfix: '/5.0', decimalPlaces: 1 },
    wellnessScore: { name: 'General Wellness', valuePostfix: '/100', decimalPlaces: 0 },
    snr: { name: 'SNR', valuePostfix: ' dB' },
};

const formatMeasurement = (measurement: DataListItem) => {
    const cm = Number(measurement.value) / 10;
    const map = MeasurementNameMap;
    const formatted: DataListItem = { name: map[measurement.key!], value: `${cm.toFixed(1)}\u00A0cm` };
    return formatted;
};

const formatMeasurementData = (rawData: any) => {
    if (rawData) {
        const measurements = rawData as DataListItem[];
        if (measurements === null || measurements === undefined) {
            return [] as DataListItem[];
        }
        return measurements.map(formatMeasurement)?.filter((item) => item.name !== undefined);
    }
    return [];
};

export const getDialGroup = (name: string, value: number, sex?: string) => {
    switch (name) {
        case 'heartRate':
            if (value >= 60 && value < 100) return DialGroup.Excellent;
            return DialGroup.Average;
        case 'breathing':
            if (value >= 12 && value < 25) return DialGroup.Excellent;
            return DialGroup.Average;
        case 'heartRateVariability':
            if (value < 10.8) return DialGroup.NeedsAttention;
            if (value < 16.4) return DialGroup.Poor;
            if (value < 35.5) return DialGroup.Average;
            if (value < 51.5) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'cardiacWorkload':
            if (value < 3.8) return DialGroup.Excellent;
            if (value < 3.9) return DialGroup.Good;
            if (value < 4.08) return DialGroup.Average;
            if (value < 4.18) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'vascularCapacity':
            if (value < 0.79) return DialGroup.NeedsAttention;
            if (value < 1.12) return DialGroup.Poor;
            if (value < 1.78) return DialGroup.Average;
            if (value < 2.11) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'bmi':
            if (value < 18.5) return DialGroup.Average;
            if (value < 25.0) return DialGroup.Excellent;
            if (value < 30.0) return DialGroup.Average;
            if (value < 35.0) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'waistToHeight':
            if (value < 43) return DialGroup.Average;
            if (value < 53) return DialGroup.Excellent;
            if (value < 58) return DialGroup.Average;
            if (value < 63) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'bodyShape':
            if (sex === 'Male') {
                if (value < 7.10) return DialGroup.Excellent;
                if (value < 7.60) return DialGroup.Good;
                if (value < 8.60) return DialGroup.Average;
                if (value < 9.10) return DialGroup.Poor;
                return DialGroup.NeedsAttention;
            }
            if (value < 6.80) return DialGroup.Excellent;
            if (value < 7.40) return DialGroup.Good;
            if (value < 8.60) return DialGroup.Average;
            if (value < 9.20) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'wellnessScore':
            if (value < 20) return DialGroup.NeedsAttention;
            if (value < 40) return DialGroup.Poor;
            if (value < 60) return DialGroup.Average;
            if (value < 80) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'mentalStressIndex':
            if (value < 2) return DialGroup.Excellent;
            if (value < 3) return DialGroup.Good;
            if (value < 4) return DialGroup.Average;
            if (value < 5) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        default:
            return undefined;
    }
};

export const formatWellnessData = (rawData: Measurement[]) => {
    if (rawData) {
        const map = rawData.reduce((acc, measurement) => {
            if (measurement.key && measurement.value !== undefined) {
                acc[measurement.key] = measurement.value;
            }
            return acc;
        }, {} as { [key: string]: number });

        return Object.keys(map).reduce((a, c) => {
            if (map[c] && !(wellnessReference[c]?.hidden)) {
                const isNumber = typeof map[c] === 'number';
                // default to 1 decimal place if not specified
                const decimalPlaces = wellnessReference[c].decimalPlaces ?? 1;
                const val = isNumber ? map[c].toFixed(decimalPlaces) : String(map[c]);
                a[c] = {
                    name: wellnessReference[c]?.name ?? c,
                    value: map[c],
                    formattedValue: val,
                    formattedWithPostfix: `${val}${wellnessReference[c]?.valuePostfix ?? ''}`,
                    dialGroup: getDialGroup(c, map[c], String(map.gender)),
                    unitLabel: wellnessReference[c].unit,
                    min: wellnessReference[c].min,
                    max: wellnessReference[c].max,
                };
            }
            return a;
        }, {} as { [key: string]: WellnessItem });
    }
    return {};
};

export const clamp = (value: number, max: number) => {
    if (value > max) return max;
    return value;
};

export default formatMeasurementData;
